import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { useMatch, Link } from 'react-router-dom';
import { H3, useLocalizeMessage, Spinner, ModalWindow, Text } from 'libs.nucleus';
import { PageHeader, PageWrapper, useBooleanState } from 'libs.react';
import { ActivitiesList } from '../../components/activities_list/activities_list.component';
import { ActivityView } from '../../components/activity/activity_modal/activity_view';
import { VisitActivityResponses } from '../../components/visit_activity_responses/visit_activity_responses.component';
import { UserLocaleContext } from '../../contexts/user_locale/user_locale.context';
import { getRouteLink } from '../../helpers/links.helpers';
import { getNonVisitGroups, getOnboardingData } from '../../helpers/non_visit_groups.helpers';
import { getParticipantDetails, getParticipantNumber, isParticipantDeactivated, isParticipantInactive, } from '../../helpers/participants.helpers';
import { getOnboardingConfig } from '../../helpers/study.helpers';
import { getVisitActivityResponsesData } from '../../helpers/visit.helpers';
import { useAxonAccountContext } from '../../hooks/use_axon_account_context';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useOrgContext } from '../../hooks/use_org_context';
import { useSiteContext } from '../../hooks/use_site_context';
export const NonVisitDetailsPage = () => {
    const [isActivityListOpen, setIsActivityListModalOpen, setIsActivityListModalClosed] = useBooleanState(false);
    const [isActivityModalOpen, setIsActivityModalOpen, setIsActivityModalClosed] = useBooleanState(false);
    const [visitActivity, setVisitActivity] = useState({});
    const match = useMatch('/site/study/:studyId/participants/:participantId/non-visit/:groupId');
    const participantId = match?.params.participantId;
    const paramGroupId = match?.params.groupId;
    const { environment } = useCurrentEnvironment();
    const site = useSiteContext();
    const org = useOrgContext();
    const translate = useLocalizeMessage();
    const queryClient = useQueryClient();
    const { getUserSelectedLocale } = useContext(UserLocaleContext);
    const siteUserLocale = getUserSelectedLocale();
    const account = useAxonAccountContext();
    const onboardingConfig = getOnboardingConfig(org);
    const onboardingGroupId = onboardingConfig?.c_group_id;
    const onboardingDisplayName = onboardingConfig?.c_display_name;
    const isOnboarding = paramGroupId === onboardingGroupId;
    const groupId = isOnboarding ? onboardingGroupId : paramGroupId;
    const getParticipantData = async () => {
        if (!environment) {
            return [];
        }
        return await getParticipantDetails({
            environment,
            siteId: site?._id ?? '',
            participantId: participantId ?? '',
        });
    };
    const { data: participantDetails, isLoading: isParticipantLoading } = useQuery({
        queryKey: ['participantDetails', participantId],
        queryFn: getParticipantData,
        enabled: !queryClient.getQueryData(['participantDetails', participantId]),
    });
    const getVisitActivityResponses = async () => {
        if (!environment) {
            return [];
        }
        const response = await getVisitActivityResponsesData({
            environment,
            participantId: participantId ?? '',
            accountId: account._id,
            siteId: site?._id ?? '',
            groupId,
        });
        return response?.data.data || [];
    };
    const { data: nonVisitDetails, isLoading: isNonVisitLoading } = useQuery({
        queryKey: ['nonVisitDetails', 'nonVisit', participantId, siteUserLocale],
        queryFn: async () => {
            // Check if the non-visit data is already cached
            const cachedNonVisits = participantDetails
                ? await queryClient.getQueryData(['nonVisitGroups', site?._id, participantId, siteUserLocale])
                : undefined;
            let nonVisits = cachedNonVisits;
            if (!nonVisits) {
                nonVisits = await getNonVisitGroups({
                    environment: environment,
                    siteId: site?._id ?? '',
                    participantId: participantId ?? '',
                    siteUserLocale,
                });
            }
            return nonVisits?.find((group) => group.c_group === groupId);
        },
        enabled: !isOnboarding,
    });
    const { data: onboardingDetails, isLoading: isOnboardingLoading } = useQuery({
        queryKey: ['onboardingActivities', onboardingGroupId, siteUserLocale],
        queryFn: () => getOnboardingData({
            environment: environment,
            siteId: site?._id ?? '',
            participantId: participantId ?? '',
            siteUserLocale,
            onboardingGroupId: onboardingGroupId ?? '',
        }),
        enabled: isOnboarding,
    });
    const { data: visitActivityResponses, isLoading: isActivityResponsesLoading } = useQuery({
        queryKey: ['getVisitActivityResponses', account._id, site._id, groupId, participantId],
        queryFn: getVisitActivityResponses,
        enabled: true,
    });
    const invalidateCaches = () => {
        queryClient.invalidateQueries({
            queryKey: ['getVisitActivityResponses', account._id, site._id, groupId, participantId],
        });
        if (isOnboarding) {
            queryClient.invalidateQueries({
                queryKey: ['onboardingActivities', onboardingGroupId, siteUserLocale],
            });
        }
        else {
            queryClient.invalidateQueries({
                queryKey: ['nonVisitDetails', 'nonVisit', participantId, siteUserLocale],
            });
        }
    };
    if (isParticipantLoading || isNonVisitLoading || isOnboardingLoading) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    const headerActions = [];
    if (!isParticipantDeactivated(participantDetails) && !isParticipantInactive(participantDetails)) {
        headerActions.push({
            name: translate('action.visit.view'),
            onClick: () => setIsActivityListModalOpen(),
            variant: 'primary',
        });
    }
    const nonVisitToView = isOnboarding
        ? {
            c_visit: '',
            c_name: onboardingDisplayName ?? '',
            c_group: onboardingGroupId ?? '',
            c_window_start: Date.now(),
            c_window_end: Date.now(),
            c_open_queries: 0,
            c_completed_assignments: 0,
            c_completed_assignments_for_site: 0,
            c_sequence: 0,
            assignments: onboardingDetails?.length ?? 0,
            total_activities_count: onboardingDetails?.length ?? 0,
            customVisitType: 'onboarding',
        }
        : nonVisitDetails;
    return (_jsxs(PageWrapper, { children: [_jsx(PageHeader, { breadcrumbPages: [
                    { name: translate('generic.participants'), path: getRouteLink(`participants`, environment) },
                    {
                        name: getParticipantNumber(participantDetails),
                        path: getRouteLink(`participants/${participantId}#visits`, environment),
                    },
                    {
                        name: isOnboarding ? (onboardingDisplayName ?? '') : nonVisitDetails.c_name || '',
                        path: '',
                    },
                ], options: headerActions, LinkComponent: Link, title: _jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(H3, { className: 'font-sans nucleus-text-h3 font-normal m-0 text-4xl font-light text-text-primary', children: isOnboarding ? (onboardingDisplayName ?? '') : nonVisitDetails.c_name || '' }), _jsx(Text, { className: 'text-md text-gray-500', children: getParticipantNumber(participantDetails) })] }) }), _jsx(VisitActivityResponses, { isLoading: isActivityResponsesLoading, visitActivityResponses: visitActivityResponses ?? [] }), !isActivityModalOpen && (_jsx(ModalWindow, { title: isOnboarding ? (onboardingDisplayName ?? '') : nonVisitDetails.c_name || '', subtitle: '', isOpen: isActivityListOpen, hideCloseIcon: true, closeWindow: () => { }, footerSecondaryActionButtons: [
                    {
                        disabled: false,
                        label: translate('action.visits.exit'),
                        content: undefined,
                        onClick: () => {
                            invalidateCaches();
                            setIsActivityListModalClosed();
                        },
                    },
                ], width: 'full', removeBodyPadding: true, children: _jsx(ActivitiesList, { visit: nonVisitToView, setVisitActivity: setVisitActivity, setIsActivityModalOpen: setIsActivityModalOpen, onActionComplete: invalidateCaches, participant: participantDetails, ...(isOnboarding && {
                        isOnboarding: true,
                        onboardingSchedule: onboardingDetails,
                    }), isNonVisitSchedule: true }) })), _jsx(ModalWindow, { title: visitActivity.activity?.c_group_task ? visitActivity.activity.c_group_task.c_assignment.c_name : '', subtitle: '', isOpen: isActivityModalOpen, hideCloseIcon: true, closeWindow: () => { }, width: 'full', children: _jsx(ActivityView, { visitActivity: visitActivity, setIsActivityModalClosed: setIsActivityModalClosed, participant: participantDetails }) })] }));
};
